import debounce from 'lodash/debounce';

import { warmUpCodeSearch } from '@atlassian/search-client/code-search-warmup';
import { isCodeSearchEnabled } from '@atlassian/search-common/code-search-mode';

import { CODE_HOST_PRODUCT_KEYS, type ProductKeys } from '../../constants/product-keys';

export const CODE_SEARCH_WARMUP_DEBOUNCE_MS = 10_000;

/**
 * `leading: true, trailing: false` — the first hover fires immediately and the next 10 seconds of
 * hovers are dropped rather than queued. That stops a pointer swiped across the filter from firing
 * a request per code host it crosses, while still getting the request away before the user clicks
 * through and searches. A trailing edge would land the warm-up after that search.
 *
 * Module-level rather than the `useDebouncedCallback` hook: a hook debounces per component
 * instance, and the app filter renders one component per option across four surfaces, so each
 * would get its own timer. One shared timer is what actually caps the load.
 */
const debouncedWarmUp = debounce(
	() => {
		void warmUpCodeSearch();
	},
	CODE_SEARCH_WARMUP_DEBOUNCE_MS,
	{ leading: true, trailing: false },
);

/**
 * Primes DevAI's per-user access caches when a code host is hovered in an app filter, so the code
 * search that follows skips the permission resolution that dominates DevAI's latency.
 *
 * The debounce is checked last because it consumes the window as a side effect.
 */
export const warmUpCodeSearchForHoveredProduct = (productKey: string): void => {
	if (!isCodeSearchEnabled()) {
		return;
	}
	if (!CODE_HOST_PRODUCT_KEYS.includes(productKey as ProductKeys)) {
		return;
	}

	debouncedWarmUp();
};

/** Test seam — drops any pending window so each test starts on a leading edge. */
export const resetCodeSearchWarmupDebounceForTests = (): void => {
	debouncedWarmUp.cancel();
};
